import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  Loader2,
  Pin,
  Plus,
  Printer,
  Search,
  Table2,
  X,
} from "lucide-react";
import { saveAs } from "file-saver";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import {
  invoiceTypeDetailsOptions,
  invoiceTypesOptions,
  receivableStatusesOptions,
} from "~/functions/selectPopoverOptions";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";

const STORAGE_KEY = "receivables_pinned_filters";
const STORAGE_PINNED_KEY = "receivables_filters_pinned";
const STUDENT_PAGE_SIZE = 10;

const EMPTY_FILTERS = {
  statusInput: "",
  statuses: [],
  studentStatus: "",
  studentId: "",
  studentName: "",
  paymentMethodId: "",
  showSummary: false,
  invoiceType: "",
  invoiceTypeDetail: "",
  autopay: "",
  entryDateFrom: "",
  entryDateTo: "",
  dueDateFrom: "",
  dueDateTo: "",
  settlementFrom: "",
  settlementTo: "",
  cashDateFrom: "",
  cashDateTo: "",
  paymentMethodFromSettlement: false,
};

const DEFAULT_FORM = EMPTY_FILTERS;

const normalizeStoredFilters = (stored) => {
  const filters = stored && typeof stored === "object" ? stored : {};
  return {
    ...EMPTY_FILTERS,
    ...filters,
    studentName: filters.studentId ? filters.studentName || "" : "",
  };
};

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeStoredFilters(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

const autopayOptions = [
  { value: "true", label: "Autopay enabled" },
  { value: "false", label: "Autopay disabled" },
];

const studentStatusOptions = [
  { value: "In Class", label: "In Class" },
  { value: "Waiting", label: "Waiting" },
  { value: "Inactive", label: "Inactive" },
];

const DateInputWithClear = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-gray-400 uppercase">
      {label}
    </label>
    <div className="relative w-full">
      <input
        type="date"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
        value={value || ""}
        onChange={onChange}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange({ target: { value: "" } })}
          className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors bg-white hover:bg-gray-100"
          title="Clear date"
        >
          <X size={14} />
        </button>
      )}
    </div>
  </div>
);

const SearchableSelect = ({
  label,
  options,
  value,
  placeholder = "Select an option",
  hideLabel = false,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label ?? "").toLowerCase().includes(String(query ?? "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      {!hideLabel && (
        <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  applying = false,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? normalizeStoredFilters(JSON.parse(raw)) : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(
    () => localStorage.getItem(STORAGE_PINNED_KEY) === "true"
  );
  const [isClosing, setIsClosing] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentResultsCount, setStudentResultsCount] = useState(0);
  const [studentPage, setStudentPage] = useState(1);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const studentListRef = useRef(null);
  const studentTotalPages = Math.max(1, Math.ceil(studentResultsCount / STUDENT_PAGE_SIZE));
  const studentPageNumbers = [...new Set([
    1,
    studentPage - 1,
    studentPage,
    studentPage + 1,
    studentTotalPages,
  ])].filter((page) => page >= 1 && page <= studentTotalPages).sort((a, b) => a - b);

  useEffect(() => {
    if (!opened || !studentPickerOpen) return;

    let active = true;
    setStudentsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/students", {
          params: {
            limit: STUDENT_PAGE_SIZE,
            page: studentPage,
            orderBy: "name",
            orderASC: "ASC",
            search: studentSearch.trim(),
          },
        });
        if (active) {
          setStudentResults(data?.rows || []);
          setStudentResultsCount(data?.totalRows || 0);
        }
      } catch (error) {
        if (active) {
          setStudentResults([]);
          setStudentResultsCount(0);
          toast.error("Failed to load students.");
        }
      } finally {
        if (active) setStudentsLoading(false);
      }
    }, studentPage === 1 ? 250 : 0);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [opened, studentPickerOpen, studentSearch, studentPage]);

  const handleTogglePin = () => {
    if (!pinned && !requireValidPaymentMethod()) return;
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  };

  useEffect(() => {
    if (opened) {
      setIsClosing(false);
      api.get("/paymentmethods/options")
        .then(({ data }) => setPaymentMethods(data || []))
        .catch((error) => {
          console.log("Failed to load payment methods:", error);
          toast.error("Failed to load payment methods.");
        });
    }
  }, [opened]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getStatusOption = (value = "") => {
    const normalized = (value || "").trim().toLowerCase();
    if (!normalized) return null;
    return receivableStatusesOptions.find(
      (option) =>
        option.value.toLowerCase() === normalized ||
        option.label.toLowerCase() === normalized
    );
  };

  const addStatus = () => {
    const selectedStatus = getStatusOption(formData.statusInput);
    if (!selectedStatus) {
      toast("Select a valid status before adding.", {
        type: "error",
        autoClose: 2500,
      });
      return;
    }

    setFormData((prev) => {
      if (prev.statuses.includes(selectedStatus.value)) {
        return { ...prev, statusInput: "" };
      }

      return {
        ...prev,
        statusInput: "",
        statuses: [...prev.statuses, selectedStatus.value],
      };
    });
  };

  const removeStatus = (status) => {
    setFormData((prev) => ({
      ...prev,
      statuses: prev.statuses.filter((item) => item !== status),
    }));
  };

  const buildStatusesCsv = () => formData.statuses.filter(Boolean).join(",");

  const paymentMethodOptions = paymentMethods.map((method) => ({
    value: method.id,
    label: method.description,
  }));
  const isPaymentMethodSelectionValid = () =>
    !formData.paymentMethodId ||
    paymentMethodOptions.some((option) => option.value === formData.paymentMethodId);

  const requireValidPaymentMethod = () => {
    if (isPaymentMethodSelectionValid()) return true;
    toast.error("Select a payment method from the list.");
    return false;
  };

  const buildQueryString = () => {
    const statusesCsv = buildStatusesCsv();
    const params = new URLSearchParams();
    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", "invoice_number");
    params.set("orderASC", "DESC");
    params.set("search", "");
    params.set("statuses", statusesCsv || "null");
    params.set("status", statusesCsv || "null");
    params.set("student_status", formData.studentStatus || "null");
    params.set("student_id", formData.studentId || "null");
    params.set("student_name", formData.studentName?.trim() || "null");
    params.set("paymentmethod_id", formData.paymentMethodId || "null");
    if (formData.showSummary) params.set("show_summary", "true");
    params.set("type", formData.invoiceType || "null");
    params.set("type_detail", formData.invoiceTypeDetail || "null");
    params.set("is_autopay", formData.autopay || "null");
    params.set("entry_date_from", formData.entryDateFrom || "null");
    params.set("entry_date_to", formData.entryDateTo || "null");
    params.set("due_date_from", formData.dueDateFrom || "null");
    params.set("due_date_to", formData.dueDateTo || "null");
    params.set("settlement_from", formData.settlementFrom || "null");
    params.set("settlement_to", formData.settlementTo || "null");
    params.set("cash_date_from", formData.cashDateFrom || "null");
    params.set("cash_date_to", formData.cashDateTo || "null");
    params.set("payment_method_from_settlement", String(Boolean(formData.paymentMethodFromSettlement)));

    return `/receivables?${params.toString()}`;
  };

  const handleApply = () => {
    if (!requireValidPaymentMethod()) return;
    if (pinned) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
    const query = buildQueryString();
    console.log("Receivables filters query:", query);
    onApplyFilters(query);
    handleClose();
  };

  const handleClear = () => {
    setFormData(DEFAULT_FORM);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleExportExcel = async () => {
    if (!requireValidPaymentMethod()) return;
    setExportingExcel(true);
    try {
      const statusesCsv = buildStatusesCsv();
      const payload = {
        entry_date_from: formData.entryDateFrom || null,
        entry_date_to: formData.entryDateTo || null,
        due_date_from: formData.dueDateFrom || null,
        due_date_to: formData.dueDateTo || null,
        settlement_from: formData.settlementFrom || null,
        settlement_to: formData.settlementTo || null,
        cash_date_from: formData.cashDateFrom || null,
        cash_date_to: formData.cashDateTo || null,
        payment_method_from_settlement: Boolean(formData.paymentMethodFromSettlement),
        statuses: formData.statuses,
        status: statusesCsv || "All",
        student_status: formData.studentStatus || "All",
        student_id: formData.studentId || null,
        student_name: formData.studentName?.trim() || null,
        paymentmethod_id: formData.paymentMethodId || null,
        type: formData.invoiceType || "All",
        type_detail: formData.invoiceTypeDetail || "All",
      };
      if (formData.autopay) {
        payload.is_autopay = formData.autopay;
      }
      const { data } = await api.post("/receivables/excel", payload);
      saveAs(`${baseURL}/get-file/${data.name}`, `${data.name}.xlsx`);
    } catch (err) {
      console.log(err);
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePrint = async () => {
    if (!requireValidPaymentMethod()) return;
    setGeneratingPdf(true);
    try {
      const statusesCsv = buildStatusesCsv();
      const payload = {
        entry_date_from: formData.entryDateFrom || null,
        entry_date_to: formData.entryDateTo || null,
        due_date_from: formData.dueDateFrom || null,
        due_date_to: formData.dueDateTo || null,
        settlement_from: formData.settlementFrom || null,
        settlement_to: formData.settlementTo || null,
        cash_date_from: formData.cashDateFrom || null,
        cash_date_to: formData.cashDateTo || null,
        payment_method_from_settlement: Boolean(formData.paymentMethodFromSettlement),
        statuses: formData.statuses,
        status: statusesCsv || "All",
        student_status: formData.studentStatus || "All",
        student_id: formData.studentId || null,
        student_name: formData.studentName?.trim() || null,
        paymentmethod_id: formData.paymentMethodId || null,
        type: formData.invoiceType || "All",
        type_detail: formData.invoiceTypeDetail || "All",
      };
      if (formData.autopay) {
        payload.is_autopay = formData.autopay;
      }
      const response = await api.post("/receivables/pdf", payload, {
        responseType: "blob",
      });
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `receivables_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div
          className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
            isClosing ? "modal-slide-out" : "modal-slide-in"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">
                Filters
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${
                  pinned
                    ? "bg-orange-100 text-orange-600 hover:bg-orange-200"
                    : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                }`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-3">
                <DateInputWithClear
                  label="Entry Date From"
                  value={formData.entryDateFrom}
                  onChange={(e) => handleChange("entryDateFrom", e.target.value)}
                />
                <DateInputWithClear
                  label="Entry Date To"
                  value={formData.entryDateTo}
                  onChange={(e) => handleChange("entryDateTo", e.target.value)}
                />
                <DateInputWithClear
                  label="Due Date From"
                  value={formData.dueDateFrom}
                  onChange={(e) => handleChange("dueDateFrom", e.target.value)}
                />
                <DateInputWithClear
                  label="Due Date To"
                  value={formData.dueDateTo}
                  onChange={(e) => handleChange("dueDateTo", e.target.value)}
                />
                <DateInputWithClear
                  label="Settlement Date From"
                  value={formData.settlementFrom}
                  onChange={(e) =>
                    handleChange("settlementFrom", e.target.value)
                  }
                />
                <DateInputWithClear
                  label="Settlement Date To"
                  value={formData.settlementTo}
                  onChange={(e) => handleChange("settlementTo", e.target.value)}
                />
                <DateInputWithClear label="Cash Date From" value={formData.cashDateFrom}
                  onChange={(e) => handleChange("cashDateFrom", e.target.value)} />
                <DateInputWithClear label="Cash Date To" value={formData.cashDateTo}
                  onChange={(e) => handleChange("cashDateTo", e.target.value)} />

              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Status
                </label>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      label="Status"
                      options={receivableStatusesOptions}
                      value={formData.statusInput}
                      onChange={(val) => handleChange("statusInput", val)}
                      placeholder="Select a status to add"
                      hideLabel
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addStatus}
                    className="w-10 shrink-0 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center justify-center"
                    title="Add status"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                {formData.statuses.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.statuses.map((status) => (
                      <span
                        key={status}
                        className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-800 px-3 py-1 text-xs font-medium"
                      >
                        {status}
                        <button
                          type="button"
                          onClick={() => removeStatus(status)}
                          className="text-orange-700 hover:text-red-600 transition-colors"
                          title={`Remove ${status}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <SearchableSelect
                label="Student Status"
                options={studentStatusOptions}
                value={formData.studentStatus}
                onChange={(val) => handleChange("studentStatus", val)}
                placeholder="Select the student status"
              />
              <div className="flex flex-col gap-1.5">
                <label htmlFor="receivables-student-name" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Student
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="receivables-student-name"
                    type="text"
                    readOnly
                    value={formData.studentName || ""}
                    placeholder="Select a student"
                    className="min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 bg-white"
                  />
                  {formData.studentId && (
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, studentId: "", studentName: "" }))}
                      aria-label="Clear selected student"
                      title="Clear selected student"
                      className="w-10 h-10 shrink-0 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer inline-flex items-center justify-center"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setStudentSearch("");
                      setStudentPage(1);
                      setStudentPickerOpen(true);
                    }}
                    aria-label="Find student"
                    title="Find student"
                    className="w-10 h-10 shrink-0 rounded-md border border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-400 cursor-pointer inline-flex items-center justify-center"
                  >
                    <Search size={18} />
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input type="checkbox" checked={Boolean(formData.paymentMethodFromSettlement)}
                  onChange={(event) => handleChange("paymentMethodFromSettlement", event.target.checked)}
                  className="accent-orange-500" />
                Use payment method from Settlement
              </label>
              <SearchableSelect
                label="Payment Method"
                options={paymentMethodOptions}
                value={formData.paymentMethodId || ""}
                onChange={(value) => handleChange("paymentMethodId", value)}
                placeholder="Search payment methods"
              />
              <SearchableSelect
                label="Invoice Type"
                options={invoiceTypesOptions}
                value={formData.invoiceType}
                onChange={(val) => handleChange("invoiceType", val)}
              />
              <SearchableSelect
                label="Type Detail"
                options={invoiceTypeDetailsOptions}
                value={formData.invoiceTypeDetail}
                onChange={(val) => handleChange("invoiceTypeDetail", val)}
              />
              <SearchableSelect
                label="Autopay"
                options={autopayOptions}
                value={formData.autopay}
                onChange={(val) => handleChange("autopay", val)}
              />
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(formData.showSummary)}
                  onChange={(event) => handleChange("showSummary", event.target.checked)}
                  className="accent-orange-500 cursor-pointer"
                />
                Show summary for all filtered receivables
              </label>
            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleExportExcel}
                className="py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                disabled={exportingExcel}
              >
                {exportingExcel ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Table2 size={16} />
                    Excel
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                disabled={generatingPdf}
              >
                {generatingPdf ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Printer size={16} />
                    Print
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors"
            >
              Clean All Filters
            </button>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applying}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {applying && <Loader2 size={16} className="animate-spin" />}
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {studentPickerOpen && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 9999, backgroundColor: "rgba(15, 23, 42, 0.6)" }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setStudentPickerOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="receivables-student-picker-title"
            className="flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
            style={{ maxWidth: 720, maxHeight: "80vh" }}
            onKeyDown={(event) => {
              if (event.key === "Escape") setStudentPickerOpen(false);
            }}
          >
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-4">
              <div>
                <h3 id="receivables-student-picker-title" className="text-base font-semibold text-gray-900">Select student</h3>
                <p className="text-xs text-gray-500">Search and choose the exact student for this filter.</p>
              </div>
              <button
                type="button"
                onClick={() => setStudentPickerOpen(false)}
                aria-label="Close student search"
                className="rounded-md p-1 text-gray-600 hover:bg-gray-200 cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>
            <div className="border-b border-gray-200 p-5">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={studentSearch}
                  onChange={(event) => {
                    setStudentSearch(event.target.value);
                    setStudentPage(1);
                    if (studentListRef.current) studentListRef.current.scrollTop = 0;
                  }}
                  placeholder="Search name or registration number"
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            <div ref={studentListRef} className="min-h-0 flex-1 overflow-y-auto">
              {studentsLoading ? (
                <div className="flex items-center justify-center gap-2 p-8 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin" /> Loading students...
                </div>
              ) : studentResults.length === 0 ? (
                <p className="p-8 text-center text-sm text-gray-500">No students found.</p>
              ) : (
                <table className="w-full table-auto text-left text-sm">
                  <thead className="sticky top-0 bg-gray-100 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Student</th>
                      <th className="px-5 py-3 font-semibold">Registration number</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentResults.map((student) => {
                      const fullName = [student.name, student.middle_name, student.last_name]
                        .filter(Boolean)
                        .join(" ");
                      const selectStudent = () => {
                        setFormData((prev) => ({
                          ...prev,
                          studentId: student.id,
                          studentName: fullName,
                        }));
                        setStudentPickerOpen(false);
                      };
                      return (
                        <tr
                          key={student.id}
                          tabIndex={0}
                          onClick={selectStudent}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              selectStudent();
                            }
                          }}
                          className="border-t border-gray-100 hover:bg-orange-50 focus:bg-orange-50 cursor-pointer"
                        >
                          <td className="px-5 py-3 text-gray-900">{fullName}</td>
                          <td className="px-5 py-3 text-gray-600">{student.registration_number || "—"}</td>
                          <td className="px-5 py-3 text-gray-600">{student.status || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {!studentsLoading && studentResultsCount > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-3 text-xs text-gray-600">
                <span>
                  Showing {(studentPage - 1) * STUDENT_PAGE_SIZE + 1}–{Math.min(studentPage * STUDENT_PAGE_SIZE, studentResultsCount)} of {studentResultsCount} students
                </span>
                {studentTotalPages > 1 && (
                  <nav aria-label="Student pages" className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={studentPage === 1 || studentsLoading}
                      onClick={() => {
                        setStudentPage((page) => page - 1);
                        if (studentListRef.current) studentListRef.current.scrollTop = 0;
                      }}
                      className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Previous
                    </button>
                    {studentPageNumbers.map((page, index) => (
                      <React.Fragment key={page}>
                        {index > 0 && page - studentPageNumbers[index - 1] > 1 && (
                          <span className="px-1">…</span>
                        )}
                        <button
                          type="button"
                          disabled={studentsLoading}
                          aria-current={page === studentPage ? "page" : undefined}
                          onClick={() => {
                            setStudentPage(page);
                            if (studentListRef.current) studentListRef.current.scrollTop = 0;
                          }}
                          className={`rounded border px-2 py-1 cursor-pointer disabled:cursor-not-allowed ${
                            page === studentPage
                              ? "border-orange-500 bg-orange-500 text-white"
                              : "border-gray-300 hover:bg-gray-100"
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                    <button
                      type="button"
                      disabled={studentPage === studentTotalPages || studentsLoading}
                      onClick={() => {
                        setStudentPage((page) => page + 1);
                        if (studentListRef.current) studentListRef.current.scrollTop = 0;
                      }}
                      className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
